import { useNavigate, useParams } from 'react-router-dom';
import { PageHeader } from '../ui/PageHeader';
import { MissingItem } from '../ui/MissingItem';
import { Panel } from '../ui/Panel';
import { Field } from '../ui/Field';
import { ImageField } from '../ui/ImageField';
import { ProductPicker } from '../ui/ProductPicker';
import { DeleteButton } from '../ui/DeleteButton';
import { undoToast } from '../ui/undoToast';
import { CheckList } from '../ui/CheckList';
import { ArticleBlockRow, blockTypes, newBlock } from './ArticleBlockRow';
import { ViewOnSite } from './ViewOnSite';
import { useEditorItem } from './useEditorItem';
import { useAdmin } from '../AdminContext';
import { SeoPanel } from '../ui/SeoPanel';
import { articleSeoChecks } from './catalogSeo';
import { articleSeoAuto } from '../../utils/seoAuto';
import { EXCERPT_MAX, articleCategories, articleChecks, emptyArticle, oneOf, readingTime } from './catalogRules';
import { btnPrimary, inputCls } from '../ui/styles';
import { move, slugify, uniqueSlug } from '../editing';
import { useStableKeys } from '../../hooks/useStableKeys';
import type { ArticleBlock } from '../../core/types/content';
const back = {
  to: '/be/journal',
  label: 'Alle artikelen'
};

/** Eén journalartikel: titel, samenvatting, tekst in blokken, genoemde producten. */
export function ArticleEditor() {
  const {
    slug = ''
  } = useParams();
  const navigate = useNavigate();
  const {
    catalog,
    putItem,
    removeItem
  } = useAdmin();
  const {
    isNew,
    item: article,
    set
  } = useEditorItem('articles', slug, emptyArticle);
  // Blokken hebben geen eigen id: vaste sleutels, zodat de cursor na verplaatsen bij het goede blok blijft.
  const blockKeys = useStableKeys(article?.body.length ?? 0);
  if (!article) return <MissingItem title="Artikel niet gevonden" back={back} data-id="element-570" data-mp="3ltfdncl8ieoa" />;
  const setBlock = (i: number, block: ArticleBlock) => set({
    body: article.body.map((b, j) => j === i ? block : b)
  });
  const {
    words,
    estimate
  } = readingTime(article);
  const add = () => {
    const created = {
      ...article,
      slug: uniqueSlug(slugify(article.title), catalog.articles.map(a => a.slug)),
      readTime: article.readTime || estimate
    };
    putItem('articles', created);
    navigate(`/be/journal/${created.slug}`, {
      replace: true
    });
  };
  return <div data-id="element-571" data-mp="33ondyl4zcd8p">
      <PageHeader title={isNew ? 'Nieuw artikel' : article.title} back={back} actions={isNew ? <button type="button" onClick={add} disabled={!article.title.trim()} className={btnPrimary} data-id="element-573" data-mp="3do4i2y4l2j2k">
              Artikel publiceren
            </button> : <>
              <DeleteButton undoable onConfirm={() => {
        const restore = removeItem('articles', article.slug);
        navigate(back.to);
        undoToast(`“${article.title || 'Naamloos artikel'}” verwijderd`, restore, {
          description: 'Pas definitief als je opslaat.',
          restored: 'Artikel teruggezet'
        });
      }} data-id="element-574" data-mp="3mhrb4ghep882" />
              <ViewOnSite path={`/journal/${article.slug}`} data-id="element-575" data-mp="ew6tf9bwenzn" />
            </>} data-id="element-572" data-mp="33odn6vc4221y" />

      <div className="grid gap-6 lg:grid-cols-12" data-id="element-576" data-mp="jw305gbzctiw">
        <div className="space-y-6 lg:col-span-8" data-id="element-577" data-mp="jtqjr7lver7y">
          <Panel data-id="element-578" data-mp="4zbxmy70f1p2">
            <div className="space-y-5" data-id="element-579" data-mp="4xr49bwfloss">
              <Field label="Titel" hint="Formuleer hem als de vraag die je in de salon krijgt; zo staat hij ook op de homepage." data-id="element-580" data-mp="50wwh00cx6sq">
                <input value={article.title} onChange={e => set({
                title: e.target.value
              })} className={`${inputCls} font-display text-[20px]`} placeholder="Bijv. Waarom trekt mijn huid in de winter?" data-id="element-581" data-mp="3sbcycdcr1ele" />
              </Field>
              <Field label="Samenvatting" hint="Staat op het overzicht én in Google." count={{
              value: article.excerpt.length,
              max: EXCERPT_MAX
            }} data-id="element-582" data-mp="3ndl61i86zqgo">
                <textarea value={article.excerpt} onChange={e => set({
                excerpt: e.target.value
              })} rows={3} className={`${inputCls} resize-y leading-relaxed`} data-id="element-583" data-mp="39f4rj5438vnz" />
              </Field>
            </div>
          </Panel>

          <Panel title="Tekst" description={`${words} woorden · ongeveer ${estimate} lezen`} data-id="element-584" data-mp="31ccl8mlqmusn">
            <ol className="space-y-3" data-id="element-585" data-mp="2wl2vzn43ptha">
              {article.body.map((block, i) => <ArticleBlockRow key={blockKeys.keys[i]} block={block} position={i + 1} total={article.body.length} onChange={b => setBlock(i, b)} onMove={dir => {
              blockKeys.move(i, i + dir);
              set({
                body: move(article.body, i, i + dir)
              });
            }} onRemove={() => {
              blockKeys.remove(i);
              set({
                body: article.body.filter((_, j) => j !== i)
              });
            }} data-id="element-586" data-mp="2rm9hbrd2vwfj" data-mpi={i} />)}
            </ol>
            <div className="mt-3 flex flex-wrap gap-2" data-id="element-587" data-mp="6vshsykbfxbc">
              {blockTypes.map(b => <button key={b.type} type="button" onClick={() => set({
              body: [...article.body, newBlock(b.type)]
            })} className="inline-flex items-center gap-1.5 rounded-full border border-dashed border-line px-3.5 py-2.5 text-[14px] text-muted transition-colors duration-150 ease-soft hover:border-ink/40 hover:text-ink sm:py-1.5 sm:text-[13px]" data-id="element-588" data-mp="fduiueiok9jh">
                  <b.icon className="h-3.5 w-3.5" strokeWidth={1.75} aria-hidden="true" data-id="element-589" data-mp="3sle14axvv0gc" />+ {b.label}
                </button>)}
            </div>
          </Panel>

          <Panel title="Genoemde producten" description="Staan naast het artikel en zijn direct in de winkelmand te leggen." data-id="element-590" data-mp="5dtxyo55xld6">
            <ProductPicker value={article.productIds ?? []} onChange={productIds => set({
            productIds
          })} products={catalog.products} emptyText="Nog geen producten gekoppeld." data-id="element-591" data-mp="af9hgbrozjtg" />
          </Panel>

          <SeoPanel seo={article.seo} onChange={seo => set({
          seo
        })} auto={articleSeoAuto(article)} path={article.slug ? `/journal/${article.slug}` : '/journal'} imageFallback="het beeld van het artikel" data-id="element-592" data-mp="w3y4uf7phjnl" />
        </div>

        <aside aria-label="Artikelinstellingen" className="space-y-6 lg:col-span-4" data-id="element-593" data-mp="113sw4jve8b96">
          <Panel title="Details" data-id="element-594" data-mp="ddyii0392inv">
            <div className="space-y-4" data-id="element-595" data-mp="df9jjs5b9cba">
              <Field label="Categorie" hint="Een nieuwe categorie wordt vanzelf een filter op de journalpagina." data-id="element-596" data-mp="8fevrxgu12ug">
                <select value={article.category} onChange={e => {
                const category = oneOf(articleCategories, e.target.value);
                if (category) set({
                  category
                });
              }} className={inputCls} data-id="element-597" data-mp="n41bw1ea9ymq">
                  {articleCategories.map(c => <option key={c} data-id="element-598" data-mp="3ls760tqx0j96">{c}</option>)}
                </select>
              </Field>
              <Field label="Datum" data-id="element-599" data-mp="21afspjhspr55">
                <input value={article.date} onChange={e => set({
                date: e.target.value
              })} className={inputCls} data-id="element-600" data-mp="2638wo55fwvg6" />
              </Field>
              <Field label="Leestijd" hint={`Geschat: ${estimate}`} data-id="element-601" data-mp="1rfw9kbzzihno">
                <input value={article.readTime} onChange={e => set({
                readTime: e.target.value
              })} className={inputCls} data-id="element-602" data-mp="1we7kkq20my7h" />
              </Field>
            </div>
          </Panel>
          <Panel title="Beeld" data-id="element-603" data-mp="2p2d4xbp5ql6s">
            <ImageField value={article.image ?? ''} onChange={image => set({
            image: image || undefined
          })} fit="cover" aspect="aspect-[4/3]" data-id="element-604" data-mp="2p0ikrzlpmx3r" />
          </Panel>
          <Panel title="Wat mist er nog?" data-id="element-605" data-mp="2f3dx1vc7utku">
            <CheckList checks={[...articleChecks(article), ...articleSeoChecks(article, catalog.articles)]} okText="Klaar om te publiceren" data-id="element-606" data-mp="2f22pdkfnh1og" />
          </Panel>
        </aside>
      </div>
    </div>;
}